<template>
  <div class="relative min-h-screen bg-gray-50 flex">
    <!-- Footer (déplacé en premier pour être derrière) -->
    <div class="fixed inset-0 pointer-events-none" style="z-index: 0;">
      <img 
        src="@/assets/footer_img.jpg" 
        alt="Zebunet footer" 
        class="absolute bottom-0 w-full h-auto object-cover" 
      />
    </div>

    <!-- Contenu principal avec barre de navigation -->
    <div class="flex-1 flex flex-col min-h-screen relative" style="z-index: 1;">
      <!-- Navigation -->
      <nav class="bg-[#ffcc33] shadow-md sticky top-0 z-50">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div class="flex justify-between h-20">
            <!-- Logo -->
            <div class="flex-shrink-0 flex items-center">
              <a href="/"><img src="@/assets/logo.png" alt="Zebunet" class="h-12 w-auto" /></a>
            </div>

            <!-- Navigation menu (desktop) -->
            <div class="hidden lg:flex items-center space-x-6">
              <router-link
                v-for="item in navigationItems"
                :key="item.path"
                :to="item.path"
                class="flex items-center px-3 py-2 text-base text-zebu-orange font-medium rounded-lg transition-colors duration-300 hover:bg-zebu-orange/10"
                :class="{ 'bg-zebu-orange/20 font-semibold': $route.path === item.path }"
              >
                <span>{{ item.name }}</span>
              </router-link>
            </div>

            <!-- Burger Menu Button (visible sur mobile et tablette) -->
            <div class="lg:hidden flex items-center">
              <button 
                @click="toggleMobileMenu"
                class="p-2 rounded-md text-zebu-orange hover:bg-zebu-orange/10 focus:outline-none focus:ring-2 focus:ring-zebu-orange focus:ring-offset-2 transition-all duration-200"
                aria-label="Menu principal"
              >
                <div class="relative w-6 h-6">
                  <span 
                    :class="[
                      'absolute left-0 block h-0.5 w-6 transform transition-all duration-300 bg-zebu-orange rounded-full',
                      isMobileMenuOpen ? 'rotate-45 top-3' : 'top-1'
                    ]"
                  ></span>
                  <span 
                    :class="[
                      'absolute left-0 block h-0.5 w-6 bg-zebu-orange rounded-full top-3',
                      isMobileMenuOpen ? 'opacity-0' : 'opacity-100'
                    ]"
                  ></span>
                  <span 
                    :class="[
                      'absolute left-0 block h-0.5 w-6 transform transition-all duration-300 bg-zebu-orange rounded-full',
                      isMobileMenuOpen ? '-rotate-45 top-3' : 'top-5'
                    ]"
                  ></span>
                </div>
              </button>
            </div>

            <!-- Actions utilisateur (visible uniquement sur desktop) -->
            <div class="hidden lg:flex items-center space-x-4">
              <!-- Panier (si connecté) -->
              <router-link
                v-if="authStore.isAuthenticated"
                to="/cart"
                class="flex items-center px-3 py-2 text-zebu-orange font-medium rounded-lg hover:bg-zebu-orange/10 transition-colors duration-300"
              >
                <span>Panier</span>
                <span v-if="cartItemsCount > 0" class="ml-2 bg-green-500 text-white text-xs font-medium px-2.5 py-0.5 rounded-full">
                  {{ cartItemsCount }}
                </span>
              </router-link>
              
              <!-- Mon compte (si connecté) -->
              <router-link
                v-if="authStore.isAuthenticated"
                to="/account"
                class="flex items-center px-3 py-2 text-zebu-orange font-medium rounded-lg hover:bg-zebu-orange/10 transition-colors duration-300"
              >
                <span>Mon compte</span>
              </router-link>
              
              <!-- Déconnexion (si connecté) -->
              <button
                v-if="authStore.isAuthenticated"
                @click="handleLogout"
                class="flex items-center px-3 py-2 text-zebu-orange font-medium rounded-lg hover:bg-zebu-orange/10 transition-colors duration-300"
              >
                <span>Déconnexion</span>
              </button>
              
              <!-- Connexion (si non-connecté) -->
              <router-link
                v-if="!authStore.isAuthenticated"
                to="/login"
                class="flex items-center px-3 py-2 text-zebu-orange font-medium rounded-lg hover:bg-zebu-orange/10 transition-colors duration-300"
              >
                <span>Connexion</span>
              </router-link>
              
              <!-- Lien vers zebu.net -->
              <a 
                href="https://zebu.net" 
                target="_blank" 
                rel="noopener noreferrer" 
                class="inline-flex items-center px-4 py-2 border border-zebu-orange rounded-lg text-sm font-medium text-zebu-orange hover:bg-zebu-orange hover:text-zebu-yellow transition-colors duration-300"
              >
                Retour sur zebu.net
                <svg class="ml-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M5.22 14.78a.75.75 0 001.06 0l7.22-7.22v5.69a.75.75 0 001.5 0v-7.5a.75.75 0 00-.75-.75h-7.5a.75.75 0 000 1.5h5.69l-7.22 7.22a.75.75 0 000 1.06z" clip-rule="evenodd" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </nav>

      <!-- Container flex pour contenu principal -->
      <div class="flex flex-1">
        <!-- Contenu principal -->
        <main :class="[
          'flex-1 min-w-0', // min-w-0 empêche le débordement
          isAuthRoute ? 'py-0' : 'py-8 pb-24 lg:pb-8'
        ]">
          <div :class="[
            isAuthRoute ? 'h-full' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'
          ]">
            <router-view></router-view>
          </div>
        </main>
      </div>

      <!-- Bulle de dialogue pour visiteurs non connectés -->
      <div v-if="!authStore.isAuthenticated && !isAuthRoute" 
        class="fixed bottom-20 left-4 lg:bottom-8 lg:left-8 max-w-xs z-50 animate-float"
      >
        <div class="relative">
          <!-- Bulle de dialogue -->
          <div class="bg-white p-4 rounded-lg shadow-xl border-2 border-zebu-orange">
            <div class="flex items-start space-x-4">
              <!-- Logo Zebu -->
              <div class="flex-shrink-0">
                <img src="@/assets/zebu.png" alt="Zebu mascotte" class="w-12 h-12 object-contain" />
              </div>
              <!-- Message -->
              <div>
                <p class="text-gray-800 font-medium">
                  Devenez adhérent Zebunet pour financer des projets
                </p>
                <router-link
                  to="/register"
                  class="inline-flex items-center mt-2 text-sm text-zebu-orange hover:text-zebu-orange-dark"
                >
                  S'inscrire
                  <svg class="ml-1 w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clip-rule="evenodd" />
                  </svg>
                </router-link>
              </div>
            </div>
          </div>
          <!-- Triangle de la bulle -->
          <div class="absolute -bottom-2 left-12 w-4 h-4 transform rotate-45 bg-white border-r-2 border-b-2 border-zebu-orange"></div>
        </div>
      </div>

      <!-- Barre de navigation fixe mobile (seulement si connecté) -->
      <div v-if="!isAuthRoute" class="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 lg:hidden" style="z-index: 40;">
        <div class="grid grid-cols-5 gap-1 p-2">
          <router-link
            v-for="item in navigationItems"
            :key="item.path"
            :to="item.path"
            class="flex flex-col items-center justify-center py-2 text-xs text-gray-600 hover:text-zebu-orange"
            :class="{ 'text-zebu-orange': $route.path === item.path }"
          >
            <!-- Icônes pour la navigation mobile -->
            <svg 
              class="h-6 w-6 mb-1" 
              xmlns="http://www.w3.org/2000/svg" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <!-- Dashboard -->
              <path 
                v-if="item.path === '/dashboard'"
                stroke-linecap="round" 
                stroke-linejoin="round" 
                stroke-width="2" 
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
              />
              <!-- Subscribe -->
              <path 
                v-if="item.path === '/subscribe'"
                stroke-linecap="round" 
                stroke-linejoin="round" 
                stroke-width="2" 
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
              <!-- Gift -->
              <path 
                v-if="item.path === '/gift'"
                stroke-linecap="round" 
                stroke-linejoin="round" 
                stroke-width="2" 
                d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"
              />
              <!-- Donate -->
              <path 
                v-if="item.path === '/donate'"
                stroke-linecap="round" 
                stroke-linejoin="round" 
                stroke-width="2" 
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
              />
              <!-- My Herd -->
              <path 
                v-if="item.path === '/my-herd'"
                stroke-linecap="round" 
                stroke-linejoin="round" 
                stroke-width="2" 
                d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
              />
            </svg>
            <span class="text-center text-[10px] font-medium">{{ item.name }}</span>
          </router-link>
        </div>
      </div>

      <!-- Menu Mobile (seulement si connecté) -->
      <div v-if="!isAuthRoute && isMobileMenuOpen" class="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40" @click="closeMobileMenu"></div>
      <div v-if="!isAuthRoute" :class="[
        'lg:hidden fixed top-0 right-0 h-full w-64 bg-zebu-orange transform transition-transform duration-300 ease-in-out z-50',
        isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
      ]">
        <!-- En-tête du menu -->
        <div class="p-4 border-b border-white/10">
          <div class="flex items-center justify-between">
            <div class="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
              <span class="text-zebu-yellow font-bold text-lg">Z</span>
            </div>
            <button 
              @click="closeMobileMenu"
              class="p-2 rounded-md text-zebu-beige hover:bg-white/10"
            >
              <svg class="w-6 h-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <!-- Navigation principale -->
        <nav class="flex-1 px-3 py-4">
          <div class="space-y-2">
            <router-link
              v-for="item in navigationItems"
              :key="item.path"
              :to="item.path"
              class="flex items-center px-3 py-2 text-base text-zebu-beige rounded-lg transition-colors duration-300 hover:bg-white/10"
              :class="{ 'bg-white/20': $route.path === item.path }"
              @click="closeMobileMenu"
            >
              <span>{{ item.name }}</span>
            </router-link>
          </div>
        </nav>

        <!-- Actions utilisateur -->
        <div class="p-4 border-t border-white/10">
          <template v-if="authStore.isAuthenticated">
            <!-- Panier -->
            <router-link
              to="/cart"
              class="flex items-center px-3 py-2 text-base text-zebu-beige rounded-lg transition-colors duration-300 hover:bg-white/10 mb-2"
              @click="closeMobileMenu"
            >
              <div class="flex items-center justify-between w-full">
                <span>Panier</span>
                <span v-if="cartItemsCount > 0" class="bg-green-500 text-white text-xs font-medium px-2.5 py-0.5 rounded-full">
                  {{ cartItemsCount }}
                </span>
              </div>
            </router-link>

            <!-- Mon compte -->
            <router-link
              to="/account"
              class="flex items-center px-3 py-2 text-base text-zebu-beige rounded-lg transition-colors duration-300 hover:bg-white/10 mb-2"
              @click="closeMobileMenu"
            >
              <span>Mon compte</span>
            </router-link>

            <!-- Déconnexion -->
            <button
              @click="handleLogout"
              class="w-full flex items-center px-3 py-2 text-base text-zebu-beige rounded-lg transition-colors duration-300 hover:bg-white/10"
            >
              <span>Déconnexion</span>
            </button>
          </template>
          <template v-else>
            <!-- Connexion -->
            <router-link
              to="/login"
              class="w-full flex items-center px-3 py-2 text-base text-zebu-beige rounded-lg transition-colors duration-300 hover:bg-white/10"
              @click="closeMobileMenu"
            >
              <span>Connexion</span>
            </router-link>
          </template>
        </div>

        <!-- Lien vers zebu.net -->
        <div class="p-4 border-t border-white/10">
          <a 
            href="https://zebu.net" 
            target="_blank" 
            rel="noopener noreferrer" 
            class="flex items-center px-3 py-2 text-base text-zebu-beige rounded-lg transition-colors duration-300 hover:bg-white/10"
          >
            <span>Retour sur zebu.net</span>
            <svg class="ml-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M5.22 14.78a.75.75 0 001.06 0l7.22-7.22v5.69a.75.75 0 001.5 0v-7.5a.75.75 0 00-.75-.75h-7.5a.75.75 0 000 1.5h5.69l-7.22 7.22a.75.75 0 000 1.06z" clip-rule="evenodd" />
            </svg>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { memberareaService } from '@/services/memberarea.service';
import { useAuthStore } from '@/stores/auth';

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();

const isUserMenuOpen = ref(false);
const isNotificationMenuOpen = ref(false);
const dashboardData = ref(null);
const cartItemsCount = ref(0);
const isMobileMenuOpen = ref(false);

// Vérifier si on est sur une route d'authentification
const isAuthRoute = computed(() => {
  return [
    '/login',
    '/register',
    '/forgot-password',
    '/reset-password'
  ].some(path => route.path.startsWith(path));
});

// Fonction pour mettre à jour le nombre d'articles dans le panier
const updateCartCount = async () => {
  try {
    const response = await memberareaService.getCart();
    if (response.success && response.data) {
      cartItemsCount.value = response.data.items?.length || 0;
    }
  } catch (error) {
    // Suppression du console.error
  }
};

// Watch pour mettre à jour le compteur lors des changements de route
watch(() => route.path, (newPath, oldPath) => {
  // Mettre à jour le compteur si on vient de ou on va vers le panier
  if (newPath === '/cart' || oldPath === '/cart') {
    updateCartCount();
  }
});

// Navigation items avec noms courts pour mobile
const navigationItems = computed(() => {
  const baseItems = [
    { name: 'Accueil', shortName: 'Accueil', path: '/dashboard' },
    { name: 'Souscrire', shortName: 'Souscrire', path: '/subscribe' },
    { name: 'Cadeau', shortName: 'Cadeau', path: '/gift' },
    { name: 'Don', shortName: 'Don', path: '/donate' },
  ]
  
  // Ajouter "Cheptel" seulement si l'utilisateur est connecté
  if (authStore.isAuthenticated) {
    baseItems.push({ name: 'Cheptel', shortName: 'Cheptel', path: '/my-herd' })
  }
  
  return baseItems
})

// Récupérer les données du dashboard pour la notification
onMounted(async () => {
  // Suppression des console.log de debug
  
  // Mettre à jour le compteur du panier
  await updateCartCount();
  
  try {
    const data = await memberareaService.getDashboardData();
    if (data.success) {
      dashboardData.value = data;
    }
  } catch (error) {
    // Suppression du console.error
  }
});

// Computed properties pour la notification
const daysUntilExpiration = computed(() => {
  return dashboardData.value?.subscription_status?.days_until_expiration || 0;
});

const showRenewalNotification = computed(() => {
  const daysLeft = daysUntilExpiration.value;
  return daysLeft <= 30 && daysLeft > 0;
});

// Méthodes pour gérer les menus
const toggleUserMenu = () => {
  isUserMenuOpen.value = !isUserMenuOpen.value;
  if (isUserMenuOpen.value) {
    isNotificationMenuOpen.value = false;
  }
};

const toggleNotificationMenu = () => {
  isNotificationMenuOpen.value = !isNotificationMenuOpen.value;
  if (isNotificationMenuOpen.value) {
    isUserMenuOpen.value = false;
  }
};

const closeNotificationMenu = () => {
  isNotificationMenuOpen.value = false;
};

const handleRenewal = () => {
  closeNotificationMenu();
  router.push('/subscribe');
};

const handleLogout = async () => {
  await authStore.logout();
  router.push('/login');
};

// Fermer les menus quand on clique en dehors
onMounted(() => {
  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!target.closest('.relative')) {
      isUserMenuOpen.value = false;
      isNotificationMenuOpen.value = false;
    }
  });
});

// Ajout des styles Tailwind personnalisés
const customStyles = `
  .text-zebu-beige {
    color: #faebc1;
  }
  
  .bg-zebu-orange {
    background-color: #b0510d;
  }
`

// Ajout des styles au head
onMounted(() => {
  const style = document.createElement('style')
  style.textContent = customStyles
  document.head.appendChild(style)
})

// Méthodes pour gérer le menu mobile
const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
  if (isMobileMenuOpen.value) {
    isUserMenuOpen.value = false;
    isNotificationMenuOpen.value = false;
  }
};

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false;
};
</script>

<style scoped>
.nav-link {
  @apply inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-500 hover:text-gray-900;
  @apply border-b-2 border-transparent hover:border-gray-300;
}

.nav-link-active {
  @apply text-zebu-orange border-zebu-orange;
}

/* Styles pour la barre de navigation mobile */
@media (max-width: 1023px) {
  .min-h-screen {
    padding-bottom: 5rem; /* Augmenté pour la barre de navigation mobile */
  }
  
  /* Animation pour les liens actifs */
  .router-link-active svg {
    @apply text-zebu-orange transform scale-110 transition-transform duration-200;
  }
  
  /* Effet de pression au clic */
  .router-link-active:active {
    @apply transform scale-95 transition-transform duration-100;
  }
}

/* Effet de survol sur desktop */
@media (min-width: 1024px) {
  .nav-link:hover {
    @apply text-zebu-orange transform scale-105 transition-all duration-200;
  }
}

/* Animation du menu burger */
.burger-menu-enter-active,
.burger-menu-leave-active {
  transition: transform 0.3s ease-in-out;
}

.burger-menu-enter-from,
.burger-menu-leave-to {
  transform: translateX(100%);
}

/* Styles pour le menu burger */
.burger-button {
  @apply relative z-50;
}

.burger-button span {
  @apply transition-all duration-300 ease-in-out;
}

/* Animation plus fluide pour le menu mobile */
.mobile-menu-enter-active,
.mobile-menu-leave-active {
  @apply transition-all duration-300 ease-in-out;
}

.mobile-menu-enter-from,
.mobile-menu-leave-to {
  @apply opacity-0 translate-x-full;
}

.mobile-menu-enter-to,
.mobile-menu-leave-from {
  @apply opacity-100 translate-x-0;
}

/* Amélioration de l'interaction tactile */
@media (max-width: 1023px) {
  .nav-item {
    @apply active:bg-zebu-orange/10 active:scale-95;
    -webkit-tap-highlight-color: transparent;
  }
  
  .bottom-nav-item {
    @apply active:bg-zebu-orange/5;
    -webkit-tap-highlight-color: transparent;
  }
}

/* Styles spécifiques pour les pages d'authentification */
.auth-page {
  @apply min-h-screen flex flex-col;
}

/* Ajustement de la hauteur du contenu principal pour les pages d'auth */
.auth-content {
  @apply flex-1 flex flex-col;
}

/* Animation de flottement pour la bulle */
@keyframes float {
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
  100% {
    transform: translateY(0px);
  }
}

.animate-float {
  animation: float 3s ease-in-out infinite;
}
</style> 